import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import {
  AskDialog, Button, Field, FieldRow, DatePicker, FormScroll, FormSection, HeaderPill, Icon, ListEmpty, LoadingState, Menu, MenuRow, Modal, ModalHead, Pill, Prompt, PromptActions, PromptBody,
  Radio, RadioGroup, ScrollBar, SignaturePrompt, StackList, StackListAction, Text, Textarea, Title, toastError, toastSuccess,
} from 'admination-react'
import { ApiError } from '@/core/api/http'
import { DateField } from '@/core/components/form/DateField'
import { DropCard } from '@/core/components/modal/DropCard'
import { formatDatum, todayIso } from '@/core/lib/dates'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { UPLOAD_ACCEPT } from '@/core/lib/fileTypes'
import { useApp } from '@/core/state/appStore'
import {
  anrufAnlegen, anrufEntfernen, AU_MAX_BYTES, auAnlegen, auDatei, auEntfernen, nachholenPruefen, nachholenTage, nachholenUnterschreiben,
  type AnwesenheitPerson, type AnwesenheitTag, type NachholGrund,
} from '../../api/anwesenheit'

type AuWerte = { datei: File; beginn: string; ende: string }
type Nachholen = { datum: string; grund: NachholGrund; erlaeuterung: string }
type Schritt =
  | { art: 'au-datei' }
  | ({ art: 'au-form' } & AuWerte)
  | ({ art: 'au-bestaetigen' } & AuWerte)
  | { art: 'fehlen' }
  | { art: 'eintrag'; tag: AnwesenheitTag }
  | { art: 'nachholen-liste' }
  | ({ art: 'nachholen-grund' } & Nachholen)
  | ({ art: 'nachholen-signatur' } & Nachholen)
type Offen = { person: AnwesenheitPerson; schritt: Schritt }

const GRUENDE: readonly { wert: NachholGrund; label: string }[] = [
  { wert: 'krank', label: "Krank" },
  { wert: 'unentschuldigt', label: "Unentschuldigt gefehlt" },
  { wert: 'sonstiges', label: "Sonstiges" },
  { wert: 'vergessen', label: "War anwesend, Unterschrift vergessen" },
]
const WOCHENTAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"]
const UHRZEIT = /^([01]\d|2[0-3]):[0-5]\d$/
const AU_TYP_FALSCH = "Bitte laden Sie eine PDF-, PNG- oder JPEG-Datei hoch."
const AU_ZU_GROSS = "Die Datei darf höchstens 5 MB groß sein."

const tagLabel = (iso: string): string => `${WOCHENTAGE[new Date(`${iso}T00:00:00Z`).getUTCDay()]}, ${formatDatum(iso)}`
const jetztUhrzeit = (): string => new Date().toTimeString().slice(0, 5)

const meldung = (error: unknown, allgemein: string): string =>
  error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 401 && error.detailText
    ? error.detailText.replace(/^[a-z_]+: /, '')
    : friendlyErrorMessage(error, allgemein)

const unterschriftSatz = (name: string, { datum, grund, erlaeuterung }: Nachholen): string => {
  const am = formatDatum(datum)
  if (grund === 'vergessen') return `Hiermit bestätige ich, ${name}, dass ich am ${am} vor Ort war und die Unterschrift vergessen habe.`
  const text = grund === 'sonstiges' ? erlaeuterung.trim() : GRUENDE.find(({ wert }) => wert === grund)?.label
  return `Hiermit bestätige ich, ${name}, dass ich am ${am} nicht vor Ort war aus folgendem Grund: ${text}`
}

const dateiAbgelehnt = (error: unknown): boolean =>
  error instanceof ApiError && (error.status === 413 || (error.status === 422 && error.detailText === AU_TYP_FALSCH))

function ScrollBody({ children }: { children: ReactNode }) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  return <>
    <PromptBody ref={scrollerRef} data-scroll-host>{children}</PromptBody>
    <ScrollBar scrollerRef={scrollerRef} />
  </>
}

function AuKopf({ name, onClose, aktion, size }: { name: string; onClose: () => void; aktion?: ReactNode; size: 'document' | 'form' }) {
  return <ModalHead
    modalSize={size}
    modalLabel="AU hochladen"
    left={{ type: 'title', label: name, icon: 'i-person', active: false }}
    center={<HeaderPill value={{ type: 'title', label: "AU hochladen", icon: 'i-doc' }} />}
    actions={aktion}
    onClose={onClose}
    closeLabel="Schließen"
  />
}

function FehlenPrompt({ person, onClose, onFertig }: { person: AnwesenheitPerson; onClose: () => void; onFertig: () => void }) {
  const [datum, setDatum] = useState(todayIso)
  const [zeit, setZeit] = useState(jetztUhrzeit)
  const [bis, setBis] = useState('')
  const [busy, setBusy] = useState(false)
  const datumId = useId()
  async function eintragen() {
    setBusy(true)
    try {
      await anrufAnlegen(person.coaching_id, new Date(`${datum}T${zeit}:00`).toISOString(), bis)
      onFertig()
    } catch (error) {
      toastError(meldung(error, "Das Fehlen konnte nicht eingetragen werden."))
      setBusy(false)
    }
  }
  return <Prompt aria-label="Entschuldigtes Fehlen eintragen" onEscape={onClose} busy={busy}>
    <PromptBody>
      <Title as="h3" tabIndex={-1} data-initial-focus>Entschuldigtes Fehlen eintragen</Title>
      <p className="sign-hint">{person.name}</p>
      <Field label="Wann hat die Person angerufen?" htmlFor={datumId} required>
        <FieldRow>
          <DatePicker id={datumId} value={datum} onChange={setDatum} pastYears={1} prevMonthLabel="Vorheriger Monat" nextMonthLabel="Nächster Monat" aria-label="Datum des Anrufs" />
          <Text className="calendar-call-time" value={zeit} onChange={(event) => setZeit(event.target.value)} aria-label="Uhrzeit des Anrufs" />
        </FieldRow>
      </Field>
      <DateField label="Für wie lange ist die Person krank?" isoValue={bis} onChange={setBis} pastYears={1} required hint={{ tone: 'info', text: "Krank bis einschließlich dieses Tages." }} />
    </PromptBody>
    <PromptActions>
      <Button disabled={busy} onClick={onClose}>Abbrechen</Button>
      <Button variant="primary" loading={busy} disabled={!datum || !UHRZEIT.test(zeit) || !bis} onClick={() => void eintragen()}>Eintragen</Button>
    </PromptActions>
  </Prompt>
}

function EintragPrompt({ person, tag, onClose, onFertig }: { person: AnwesenheitPerson; tag: AnwesenheitTag; onClose: () => void; onFertig: () => void }) {
  const [busy, setBusy] = useState(false)
  const id = tag.eintrag_id ?? ''
  const au = tag.status === 'au'
  const titel = `${au ? "Arbeitsunfähig" : "Entschuldigt"} vom ${formatDatum(tag.eintrag_von ?? '')} bis ${formatDatum(tag.eintrag_bis ?? '')}`
  async function entfernen() {
    setBusy(true)
    try {
      await (au ? auEntfernen(id) : anrufEntfernen(id))
    } catch (error) {
      toastError(meldung(error, "Der Eintrag konnte nicht entfernt werden."))
    }
    onFertig()
  }
  async function ansehen() {
    const fenster = window.open('', '_blank')
    try {
      const url = URL.createObjectURL(await auDatei(id))
      if (fenster) fenster.location.href = url
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (error) {
      fenster?.close()
      toastError(meldung(error, "Die AU konnte nicht geöffnet werden."))
    }
  }
  return <Prompt aria-label={au ? "Arbeitsunfähigkeit" : "Entschuldigtes Fehlen"} onEscape={onClose} busy={busy}>
    <PromptBody>
      <Title as="h3">{titel}</Title>
      <p className="sign-hint">{person.name}{" · eingetragen"}{tag.eingetragen_von ? ` von ${tag.eingetragen_von}` : ''}{" am "}{formatDatum(tag.eingetragen_am ?? '')}</p>
    </PromptBody>
    <PromptActions>
      <Button variant="danger" loading={busy} onClick={() => void entfernen()}>Entfernen</Button>
      {au && <Button variant="primary" disabled={busy} onClick={() => void ansehen()}>AU ansehen</Button>}
    </PromptActions>
  </Prompt>
}

function NachholenListe({ person, onClose, onWahl }: { person: AnwesenheitPerson; onClose: () => void; onWahl: (datum: string) => void }) {
  const [tage, setTage] = useState<string[] | null>(null)
  useEffect(() => {
    let abgebrochen = false
    nachholenTage(person.coaching_id).then(
      (geladen) => { if (!abgebrochen) setTage(geladen) },
      (error: unknown) => {
        if (abgebrochen) return
        toastError(meldung(error, "Die Tage konnten nicht geladen werden."))
        onClose()
      },
    )
    return () => { abgebrochen = true }
  }, [person.coaching_id, onClose])
  return <Prompt aria-label="Unterschriften nachholen" onEscape={onClose}>
    <ScrollBody>
      <Title as="h3">Unterschriften nachholen</Title>
      <p className="sign-hint">{person.name}: Tage ohne Unterschrift</p>
      {tage === null && <LoadingState>Die Tage werden geladen …</LoadingState>}
      {tage?.length === 0 && <ListEmpty>Keine Tage ohne Unterschrift</ListEmpty>}
      {tage && tage.length > 0 && <StackList>{tage.map((tag) => <StackListAction key={tag} onClick={() => onWahl(tag)}>{tagLabel(tag)}</StackListAction>)}</StackList>}
    </ScrollBody>
  </Prompt>
}

function NachholenGrund({ person, werte, onZurueck, onWeiter }: { person: AnwesenheitPerson; werte: Nachholen; onZurueck: () => void; onWeiter: (werte: Nachholen) => void }) {
  const [grund, setGrund] = useState(werte.grund)
  const [erlaeuterung, setErlaeuterung] = useState(werte.erlaeuterung)
  const [busy, setBusy] = useState(false)
  const textId = useId()
  async function weiter() {
    setBusy(true)
    try {
      await nachholenPruefen(person.coaching_id, werte.datum)
      onWeiter({ datum: werte.datum, grund, erlaeuterung })
    } catch (error) {
      toastError(meldung(error, "Der Tag konnte nicht geprüft werden."))
      setBusy(false)
    }
  }
  return <Prompt aria-label="Grund wählen" onEscape={onZurueck} busy={busy}>
    <ScrollBody>
      <Title as="h3">Warum fehlt die Unterschrift?</Title>
      <p className="sign-hint">{person.name} · {tagLabel(werte.datum)}</p>
      <RadioGroup stacked role="radiogroup" aria-label="Grund">
        {GRUENDE.map(({ wert, label }) => <Radio key={wert} name="grund" checked={grund === wert} onChange={() => setGrund(wert)}>{label}</Radio>)}
      </RadioGroup>
      {grund === 'sonstiges' && <Field label="Bitte erläutern." htmlFor={textId}>
        <Textarea id={textId} rows={3} value={erlaeuterung} onChange={(event) => setErlaeuterung(event.target.value)} />
      </Field>}
    </ScrollBody>
    <PromptActions>
      <Button disabled={busy} onClick={onZurueck}>Zurück</Button>
      <Button variant="primary" loading={busy} disabled={grund === 'sonstiges' && !erlaeuterung.trim()} onClick={() => void weiter()}>Weiter</Button>
    </PromptActions>
  </Prompt>
}

function AuHochladen({ person, schritt, setSchritt, onClose, onFertig }: {
  person: AnwesenheitPerson
  schritt: Extract<Schritt, { art: 'au-datei' | 'au-form' | 'au-bestaetigen' }>
  setSchritt: (schritt: Schritt) => void
  onClose: () => void
  onFertig: () => void
}) {
  const [busy, setBusy] = useState(false)
  if (schritt.art === 'au-datei') return <Modal size="document" onClose={onClose} topbar={<AuKopf size="document" name={person.name} onClose={onClose} />}>
    <DropCard title="AU auswählen oder hierher ziehen" body="PDF, PNG oder JPEG, höchstens 5 MB." accept={UPLOAD_ACCEPT} loadingLabel="AU wird hochgeladen …" typeError={() => AU_TYP_FALSCH} onFile={(datei) => {
      if (datei.size > AU_MAX_BYTES) toastError(AU_ZU_GROSS)
      else setSchritt({ art: 'au-form', datei, beginn: '', ende: '' })
    }} />
  </Modal>
  const { datei, beginn, ende } = schritt
  async function bestaetigen() {
    setBusy(true)
    try {
      await auAnlegen(person.coaching_id, beginn, ende, datei)
      onFertig()
    } catch (error) {
      toastError(meldung(error, "Die AU konnte nicht hochgeladen werden."))
      setBusy(false)
      setSchritt(dateiAbgelehnt(error) ? { art: 'au-datei' } : { art: 'au-form', datei, beginn, ende })
    }
  }
  return <>
    <Modal size="form" onClose={onClose} topbar={<AuKopf size="form" name={person.name} onClose={onClose} aktion={
      <Pill><Button variant="success" disabled={!beginn || !ende} onClick={() => setSchritt({ art: 'au-bestaetigen', datei, beginn, ende })}><Icon name="i-doc" />Weiter</Button></Pill>
    } />}>
      <FormScroll>
        <FormSection as="div">
          <Title as="h2">Arbeitsunfähigkeit</Title>
          <DateField label="Arbeitsunfähigkeit Beginn" isoValue={beginn} pastYears={1} required onChange={(wert) => setSchritt({ art: 'au-form', datei, beginn: wert, ende })} />
          <DateField label="Arbeitsunfähigkeit Ende" isoValue={ende} pastYears={1} required onChange={(wert) => setSchritt({ art: 'au-form', datei, beginn, ende: wert })} />
        </FormSection>
      </FormScroll>
    </Modal>
    {schritt.art === 'au-bestaetigen' && <AskDialog
      tone="info"
      icon={<Icon name="i-calendar" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />}
      title="Ist das korrekt?"
      busy={busy}
      confirmLabel="Bestätigen"
      cancelLabel="Zurück"
      onDecide={(ja) => { if (ja) void bestaetigen(); else setSchritt({ art: 'au-form', datei, beginn, ende }) }}
    >
      {"Arbeitsunfähig vom "}{formatDatum(beginn)}{" bis "}{formatDatum(ende)}.
    </AskDialog>}
  </>
}

export function useAnwesenheitAktionen(): {
  zeilenMenue: (person: AnwesenheitPerson) => ReactNode
  eintragOeffnen: (person: AnwesenheitPerson, tag: AnwesenheitTag) => void
  dialog: ReactNode
} {
  const { reloadDashboard } = useApp()
  const [offen, setOffen] = useState<Offen | null>(null)
  const [signiert, setSigniert] = useState(false)
  const schliessen = useCallback(() => setOffen(null), [])
  const fertig = () => { schliessen(); reloadDashboard() }
  const zeilenMenue = (person: AnwesenheitPerson) => <Menu
    isList
    align="end"
    panelClassName="calendar-row-menu-panel"
    trigger={<Icon name="i-dots" />}
    triggerButtonProps={{ iconOnly: true, className: 'calendar-row-menu', 'aria-label': `Aktionen für ${person.name}`, title: "Aktionen" }}
  >
    <MenuRow icon="i-upload" label="AU hochladen" onSelect={() => setOffen({ person, schritt: { art: 'au-datei' } })} />
    <MenuRow icon="i-calendar" label="Entschuldigtes Fehlen eintragen" onSelect={() => setOffen({ person, schritt: { art: 'fehlen' } })} />
    <MenuRow icon="i-signature" label="Unterschriften nachholen" onSelect={() => setOffen({ person, schritt: { art: 'nachholen-liste' } })} />
  </Menu>

  function dialogZu({ person, schritt }: Offen): ReactNode {
    const setSchritt = (naechster: Schritt) => setOffen({ person, schritt: naechster })
    switch (schritt.art) {
      case 'au-datei':
      case 'au-form':
      case 'au-bestaetigen':
        return <AuHochladen person={person} schritt={schritt} setSchritt={setSchritt} onClose={schliessen} onFertig={fertig} />
      case 'fehlen':
        return <FehlenPrompt person={person} onClose={schliessen} onFertig={fertig} />
      case 'eintrag':
        return <EintragPrompt person={person} tag={schritt.tag} onClose={schliessen} onFertig={fertig} />
      case 'nachholen-liste':
        return <NachholenListe person={person} onClose={schliessen} onWahl={(datum) => setSchritt({ art: 'nachholen-grund', datum, grund: 'krank', erlaeuterung: '' })} />
      case 'nachholen-grund':
        return <NachholenGrund key={schritt.datum} person={person} werte={schritt} onZurueck={() => setSchritt({ art: 'nachholen-liste' })} onWeiter={(werte) => setSchritt({ art: 'nachholen-signatur', ...werte })} />
      case 'nachholen-signatur': {
        const zurueck = () => setSchritt({ ...schritt, art: 'nachholen-grund' })
        const absenden = async (png: string) => {
          setSigniert(true)
          try {
            await nachholenUnterschreiben(person.coaching_id, schritt.datum, schritt.grund, schritt.erlaeuterung, png)
            toastSuccess("Unterschrift nachgeholt.")
            reloadDashboard()
            setSchritt({ art: 'nachholen-liste' })
          } catch (error) {
            toastError(meldung(error, "Die Unterschrift konnte nicht gespeichert werden."))
            if (error instanceof ApiError && error.status === 422) zurueck()
            else setSchritt({ art: 'nachholen-liste' })
          } finally {
            setSigniert(false)
          }
        }
        return <SignaturePrompt
          title={unterschriftSatz(person.name, schritt)}
          clearLabel="Löschen"
          cancelLabel="Schließen"
          confirmLabel={<><Icon name="i-check" />Bestätigen</>}
          busy={signiert}
          onCancel={zurueck}
          onCapture={(png) => void absenden(png)}
        />
      }
    }
  }

  return {
    zeilenMenue,
    eintragOeffnen: (person, tag) => setOffen({ person, schritt: { art: 'eintrag', tag } }),
    dialog: offen && dialogZu(offen),
  }
}
